import React, { useEffect, useState } from 'react';
import { Button, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, Typography } from '@mui/material';
import {
  GetUseOfForceIncidentsForServiceProviderUnitResponse,
  UseOfForceIncidentModel,
  UseOfForceIncidentWarning,
  UseOfForceUnitOverviewAuthorizationItemModel,
  UseOfForceUnitOverviewItemModel,
} from '../../api-service/clients';
import { WindowService } from '../../components/window-service/window-service';
import { useBroadcastContext } from '../../hooks';
import { getUseOfForceUnitOverview, registerUseOfForceRelativesInformation, registerUseOfForceResponsibleMunicipalitySending } from './use-of-force-unit-overview-api';
import { UseOfForceRelativesDialog } from './use-of-force-relatives-dialog';
import { UseOfForceMunicipalitySendingDialog } from './use-of-force-municipality-sending-dialog';
import { UseOfForceReportPanel } from './use-of-force-report-panel';
import { formatCpr, formatDate, formatDateTime } from '../../utilities';
import { useServiceProviderUnits } from '../service-provider-unit-calendar/hooks/use-service-provider-units';
import { ServiceProviderRoute, ServiceProviderUrlBuilder } from '../citizen/serviceprovider/utils/service-provider-url-builder';
import { registrationDeadline } from '../citizen/serviceprovider/use-of-force/use-of-force-tab-content';
import {
  authorizationStatusLabels,
  authorizationTypeShortLabels,
  hygieneRestraintLogText,
  interventionTypeShortLabels,
} from '../citizen/serviceprovider/use-of-force/use-of-force-labels';
import { createUseOfForceSummaryRegistration } from '../citizen/serviceprovider/use-of-force/use-of-force-api';

type OverviewTab = 'awaitingApproval' | 'awaitingReport' | 'awaitingSummaryRegistration' | 'myRegistrations';

export function UseOfForceUnitOverview() {
  const { currentUnit } = useServiceProviderUnits();
  const serviceProviderUnitId = currentUnit?.id;
  const { subscribe } = useBroadcastContext('UseOfForceUpdate');
  const [overview, setOverview] = useState<GetUseOfForceIncidentsForServiceProviderUnitResponse>();
  const [selectedTab, setSelectedTab] = useState<OverviewTab>();
  const [reloadCount, setReloadCount] = useState(0);
  const [relativesItem, setRelativesItem] = useState<UseOfForceUnitOverviewItemModel>();
  const [municipalitySendingItem, setMunicipalitySendingItem] = useState<UseOfForceUnitOverviewItemModel>();

  useEffect(() => {
    subscribe(() => setReloadCount(count => count + 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!serviceProviderUnitId) return undefined;
    let isCurrent = true;
    async function load(unitId: string) {
      const response = await getUseOfForceUnitOverview(unitId);
      if (isCurrent) setOverview(response);
    }
    load(serviceProviderUnitId);
    return () => {
      isCurrent = false;
    };
  }, [serviceProviderUnitId, reloadCount]);

  if (!serviceProviderUnitId || !overview) return null;

  const tab: OverviewTab = overview.canAdminister ? selectedTab ?? 'awaitingApproval' : 'myRegistrations';

  function openIncidentOfCitizen(citizenId: string, incidentId: string) {
    WindowService.open(
      ServiceProviderUrlBuilder.build(ServiceProviderRoute.USE_OF_FORCE_INCIDENT, {
        citizenId,
        spuId: serviceProviderUnitId,
        incidentId,
      })
    );
  }

  function openIncident(item: UseOfForceUnitOverviewItemModel) {
    openIncidentOfCitizen(item.citizenId, item.incident.id);
  }

  async function openSummaryRegistration(item: UseOfForceUnitOverviewAuthorizationItemModel) {
    const registrationId =
      item.draftRegistrationId ?? (await createUseOfForceSummaryRegistration(item.citizenId, serviceProviderUnitId!, item.authorization.id)).id;
    WindowService.open(
      ServiceProviderUrlBuilder.build(ServiceProviderRoute.USE_OF_FORCE_SUMMARY_REGISTRATION, {
        citizenId: item.citizenId,
        spuId: serviceProviderUnitId,
        summaryRegistrationId: registrationId,
      })
    );
    setReloadCount(count => count + 1);
  }

  return (
    <>
      <Tabs value={tab} onChange={(_, value: OverviewTab) => setSelectedTab(value)} sx={{ mb: 2 }}>
        {overview.canAdminister && <Tab value="awaitingApproval" label={`Til godkendelse (${overview.awaitingApproval.length})`} />}
        {overview.canAdminister && <Tab value="awaitingReport" label={`Til indberetning (${overview.awaitingReport.length})`} />}
        {overview.canAdminister && (
          <Tab value="awaitingSummaryRegistration" label={`Skema 2 mangler (${overview.awaitingSummaryRegistration.length})`} />
        )}
        <Tab value="myRegistrations" label={`Mine registreringer (${overview.myRegistrations.length})`} />
      </Tabs>
      {tab === 'awaitingApproval' && (
        <IncidentTable items={overview.awaitingApproval} emptyText="Der er ingen magtanvendelser, der venter på godkendelse." onOpen={openIncident} />
      )}
      {tab === 'awaitingReport' && (
        <UseOfForceReportPanel
          serviceProviderUnitId={serviceProviderUnitId}
          awaitingReport={overview.awaitingReport}
          reloadCount={reloadCount}
          onChanged={() => setReloadCount(count => count + 1)}
          onOpenIncident={incident => openIncidentOfCitizen(incident.citizenId, incident.id)}
        >
          <IncidentTable
            items={overview.awaitingReport}
            emptyText="Der er ingen godkendte magtanvendelser, der venter på indberetning til tilsynet."
            onOpen={openIncident}
            onRegisterRelatives={setRelativesItem}
            onRegisterMunicipalitySending={setMunicipalitySendingItem}
          />
        </UseOfForceReportPanel>
      )}
      {tab === 'awaitingSummaryRegistration' && (
        <SummaryRegistrationTable items={overview.awaitingSummaryRegistration} onOpen={openSummaryRegistration} />
      )}
      {tab === 'myRegistrations' && (
        <IncidentTable items={overview.myRegistrations} emptyText="Du har ingen kladder eller returnerede magtanvendelser." showReturnComment onOpen={openIncident} />
      )}
      {relativesItem && (
        <UseOfForceRelativesDialog
          current={relativesItem.incident}
          onSave={async (informedOn, notInformed) => {
            await registerUseOfForceRelativesInformation(relativesItem.citizenId, serviceProviderUnitId, relativesItem.incident.id, informedOn, notInformed);
            setRelativesItem(undefined);
            setReloadCount(count => count + 1);
          }}
          onClose={() => setRelativesItem(undefined)}
        />
      )}
      {municipalitySendingItem && (
        <UseOfForceMunicipalitySendingDialog
          municipalityName={municipalitySendingItem.incident.approvedResponsibleMunicipalityName}
          onSave={async sentAt => {
            await registerUseOfForceResponsibleMunicipalitySending(municipalitySendingItem.citizenId, serviceProviderUnitId, municipalitySendingItem.incident.id, sentAt);
            setMunicipalitySendingItem(undefined);
            setReloadCount(count => count + 1);
          }}
          onClose={() => setMunicipalitySendingItem(undefined)}
        />
      )}
    </>
  );
}

// Decision 75: from 30 days before an afgørelse ends, or when it is revoked, until a Skema 2 covers it.
function SummaryRegistrationTable(props: {
  items: UseOfForceUnitOverviewAuthorizationItemModel[];
  onOpen: (item: UseOfForceUnitOverviewAuthorizationItemModel) => Promise<void>;
}) {
  const { items, onOpen } = props;

  if (items.length === 0) return <Typography variant="body2">Der mangler ingen samlede registreringer (Skema 2).</Typography>;

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Borger</TableCell>
          <TableCell>Afgørelse</TableCell>
          <TableCell>Periode</TableCell>
          <TableCell>Status</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map(item => (
          <TableRow key={item.authorization.id}>
            <TableCell>
              {item.citizenName ?? '-'}
              {item.citizenCpr && (
                <Typography variant="caption" display="block">
                  {formatCpr(item.citizenCpr)}
                </Typography>
              )}
            </TableCell>
            <TableCell>{authorizationTypeShortLabels[item.authorization.authorizationType]}</TableCell>
            <TableCell>
              {formatDate(item.authorization.periodFrom)} - {formatDate(item.authorization.revokedAt ?? item.authorization.periodTo)}
            </TableCell>
            <TableCell>{authorizationStatusLabels[item.authorization.status]}</TableCell>
            <TableCell align="right">
              <Button size="small" onClick={() => onOpen(item)}>
                {item.draftRegistrationId ? 'Åbn kladde' : 'Udfyld Skema 2'}
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

interface IncidentTableProps {
  items: UseOfForceUnitOverviewItemModel[];
  emptyText: string;
  showReturnComment?: boolean;
  onOpen: (item: UseOfForceUnitOverviewItemModel) => void;
  onRegisterRelatives?: (item: UseOfForceUnitOverviewItemModel) => void;
  onRegisterMunicipalitySending?: (item: UseOfForceUnitOverviewItemModel) => void;
}

function IncidentTable(props: IncidentTableProps) {
  const { items, emptyText, showReturnComment, onOpen, onRegisterRelatives, onRegisterMunicipalitySending } = props;

  if (items.length === 0) return <Typography variant="body2">{emptyText}</Typography>;

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Borger</TableCell>
          <TableCell>Indgreb påbegyndt</TableCell>
          <TableCell>Indgreb</TableCell>
          <TableCell>Frist</TableCell>
          <TableCell>Registreret af</TableCell>
          {showReturnComment && <TableCell>Returneret af leder</TableCell>}
          {onRegisterRelatives && <TableCell>Godkendt</TableCell>}
          {onRegisterMunicipalitySending && <TableCell>Handlekommune</TableCell>}
          {onRegisterRelatives && <TableCell>Pårørende orienteret</TableCell>}
          {onRegisterRelatives && <TableCell>Indberetning</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map(item => (
          <OverviewRow
            key={item.incident.id}
            item={item}
            showReturnComment={showReturnComment}
            onOpen={onOpen}
            onRegisterRelatives={onRegisterRelatives}
            onRegisterMunicipalitySending={onRegisterMunicipalitySending}
          />
        ))}
      </TableBody>
    </Table>
  );
}

interface OverviewRowProps {
  item: UseOfForceUnitOverviewItemModel;
  showReturnComment?: boolean;
  onOpen: (item: UseOfForceUnitOverviewItemModel) => void;
  onRegisterRelatives?: (item: UseOfForceUnitOverviewItemModel) => void;
  onRegisterMunicipalitySending?: (item: UseOfForceUnitOverviewItemModel) => void;
}

function OverviewRow(props: OverviewRowProps) {
  const { item, showReturnComment, onOpen, onRegisterRelatives, onRegisterMunicipalitySending } = props;
  const { citizenName, citizenCpr, incident } = item;

  return (
    <TableRow hover onClick={() => onOpen(item)} sx={{ cursor: 'pointer' }}>
      <TableCell>
        {citizenName ?? '-'}
        {citizenCpr && (
          <Typography variant="caption" display="block">
            {formatCpr(citizenCpr)}
          </Typography>
        )}
      </TableCell>
      <TableCell>{incident.draft.interventionStartedAt ? formatDateTime(incident.draft.interventionStartedAt) : '-'}</TableCell>
      <TableCell>
        {incident.draft.interventionType ? interventionTypeShortLabels[incident.draft.interventionType] : '-'}
        {item.hygieneRestraintLogStatus && (
          <Typography variant="caption" display="block">
            {hygieneRestraintLogText(item.hygieneRestraintLogStatus)}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={incident.warnings.includes(UseOfForceIncidentWarning.RegistrationDeadlineExceeded) ? { color: 'error.main' } : undefined}>
        {formatDate(registrationDeadline(incident.draft.interventionStartedAt), undefined, '-')}
      </TableCell>
      <TableCell>{incident.createdByName || '-'}</TableCell>
      {showReturnComment && <TableCell>{incident.returnComment ?? '-'}</TableCell>}
      {onRegisterRelatives && <TableCell>{formatDate(incident.approvedAt, undefined, '-')}</TableCell>}
      {onRegisterMunicipalitySending && (
        <TableCell onClick={event => event.stopPropagation()}>
          {incident.approvedResponsibleMunicipalityName ?? '-'}
          <Typography variant="caption" display="block">
            {awaitsSendingToExternalMunicipality(incident) ? (
              <Button size="small" onClick={() => onRegisterMunicipalitySending(item)}>
                Registrér afsendelse
              </Button>
            ) : (
              formatDate(incident.sentToResponsibleMunicipalityAt, 'dd-MM-yyyy HH:mm', '-')
            )}
          </Typography>
        </TableCell>
      )}
      {onRegisterRelatives && (
        <TableCell onClick={event => event.stopPropagation()}>
          <Button size="small" onClick={() => onRegisterRelatives(item)}>
            {relativesText(incident)}
          </Button>
        </TableCell>
      )}
      {onRegisterRelatives && <TableCell>{incident.reportId ? 'I kladde' : '-'}</TableCell>}
    </TableRow>
  );
}

function relativesText(incident: UseOfForceIncidentModel): string {
  if (incident.relativesNotInformed) return 'Fravigelse';
  return incident.relativesInformedOn ? formatDate(incident.relativesInformedOn) : 'Registrér';
}

function awaitsSendingToExternalMunicipality(incident: UseOfForceIncidentModel): boolean {
  return incident.responsibleMunicipalityIsOwnMunicipality === false && !incident.sentToResponsibleMunicipalityAt;
}
